import { Group, Mesh, Object3D, Scene, Vector3 } from 'three';
import { WORLD } from '../config';
import type { CharacterLoader } from '../character/CharacterModel';
import { FISH_TYPES } from './fishTypes';

/**
 * Ryby vyskakující z vody kolem splávků.
 *
 * **Synchronizované bez jediného přeneseného bajtu.**
 *
 * Skoky se negenerují náhodně a nepamatují se — počítají se jako čistá
 * funkce místa nahození a společného času světa. Každý klient si tedy
 * spočítá tytéž ryby ve stejných okamžicích, aniž by se cokoliv posílalo.
 * Po síti letí jen souřadnice splávku, a to jednou za nahození.
 *
 * Čas se bere z hodin serveru (`NetworkClient.worldTime()`), které se
 * u všech shodují na jednotky milisekund. Kdyby se použil lokální čas od
 * načtení stránky, viděl by každý hráč jiné hejno — to je přesně ta chyba,
 * kterou už jednou měla voda a mraky.
 *
 * Skoky jsou rozdělené do pevných časových přihrádek: v každé vyskočí
 * právě jedna ryba a její parametry vychází z hashe (splávek, číslo
 * přihrádky). Díky tomu se nikde nedrží stav a stačí projít přihrádky,
 * které se do současnosti ještě promítají.
 */

/** Jak často začíná nový skok. Kratší než skok sám, aby se překrývaly. */
const SLOT_SECONDS = 0.3;
/** Nejdelší možný skok — určuje, jak daleko do minulosti se hledá. */
const MAX_JUMP_SECONDS = 1.35;
/** Strop na počet ryb ve vzduchu přes všechny hráče najednou. */
const MAX_CONCURRENT = 26;

interface Spot {
  point: Vector3;
  seed: number;
  radius: number;
}

interface Jump {
  start: number;
  duration: number;
  fromX: number;
  fromZ: number;
  toX: number;
  toZ: number;
  height: number;
  spin: number;
}

export class JumpingFish {
  private readonly group = new Group();
  private readonly slots: Object3D[] = [];
  private readonly spots = new Map<string, Spot>();
  private ready = false;

  constructor(
    scene: Scene,
    private readonly loader: CharacterLoader,
  ) {
    this.group.name = 'jumpingFish';
    scene.add(this.group);
  }

  get activeSpots(): number {
    return this.spots.size;
  }

  /** Načte modely. Volat jednou, klidně dopředu. */
  async preload(): Promise<void> {
    if (this.ready) return;
    const gltf = await this.loader.loadGLTF('/models/fishing/fish.glb');

    for (let i = 0; i < MAX_CONCURRENT; i++) {
      // Druh se odvíjí od pořadí ve fondu, a přihrádka si svou schránku
      // drží — díky tomu se ryba během jednoho skoku nepromění v jiný druh.
      const type = FISH_TYPES[i % FISH_TYPES.length];
      const source = gltf.scene.getObjectByName(type.model);
      if (!source) continue;

      const fish = source.clone(true);
      fish.scale.setScalar(type.scale);
      fish.traverse((child) => {
        if (child instanceof Mesh) {
          child.castShadow = false;
          child.receiveShadow = true;
          child.frustumCulled = false;
        }
      });

      const holder = new Group();
      holder.add(fish);
      holder.visible = false;
      this.group.add(holder);
      this.slots.push(holder);
    }
    this.ready = this.slots.length > 0;
  }

  /**
   * Zapne cvrkot u splávku daného hráče.
   *
   * `id` je hráč — vlastní postava i každý vzdálený mají svůj splávek
   * a hejno kolem něj.
   */
  setSpot(id: string, point: Vector3, radius = 2.6): void {
    // Souřadnice se zaokrouhlují na čtvrtmetry: drobný rozdíl v tom, jak
    // se číslo dostalo ke klientům, by jinak posunul celé hejno jinam.
    const x = Math.round(point.x * 4) / 4;
    const z = Math.round(point.z * 4) / 4;
    this.spots.set(id, {
      point: new Vector3(x, WORLD.seaLevel, z),
      seed: hash2(Math.round(x * 4), Math.round(z * 4)),
      radius,
    });
  }

  removeSpot(id: string): void {
    this.spots.delete(id);
  }

  clear(): void {
    this.spots.clear();
    for (const slot of this.slots) slot.visible = false;
  }

  /**
   * @param worldTime společný čas světa v sekundách; offline stačí lokální,
   *        jen se pak hejno neshoduje s ostatními
   */
  update(worldTime: number): void {
    if (!this.ready) return;

    const shown = new Set<Object3D>();
    const firstSlot = Math.floor((worldTime - MAX_JUMP_SECONDS) / SLOT_SECONDS);
    const lastSlot = Math.floor(worldTime / SLOT_SECONDS);
    let spotIndex = 0;

    for (const spot of this.spots.values()) {
      for (let k = firstSlot; k <= lastSlot; k++) {
        const jump = describeJump(spot, k);
        const progress = (worldTime - jump.start) / jump.duration;
        if (progress < 0 || progress > 1) continue;

        // Schránka se vybírá podle čísla přihrádky, ne podle pořadí
        // vykreslení — jinak by se druh ryby měnil od snímku ke snímku.
        // Posun po hráčích brání tomu, aby si dva splávky sáhly na tutéž.
        const index = mod(k + spotIndex * 7, this.slots.length);
        const holder = this.slots[index];
        if (shown.has(holder)) continue;

        applyJump(holder, jump, progress);
        holder.visible = true;
        shown.add(holder);
      }
      spotIndex++;
    }

    // Schránky, na které nedošlo, musí zmizet — jinak by poslední poloha
    // ryby zůstala viset nad hladinou.
    for (const slot of this.slots) {
      if (!shown.has(slot)) slot.visible = false;
    }
  }

  dispose(): void {
    this.clear();
    this.group.removeFromParent();
  }
}

/** Parametry skoku v dané přihrádce. Čistá funkce — nedrží žádný stav. */
function describeJump(spot: Spot, slot: number): Jump {
  const r1 = random01(spot.seed, slot, 1);
  const r2 = random01(spot.seed, slot, 2);
  const r3 = random01(spot.seed, slot, 3);
  const r4 = random01(spot.seed, slot, 4);
  const r5 = random01(spot.seed, slot, 5);
  const r6 = random01(spot.seed, slot, 6);

  const angle = r1 * Math.PI * 2;
  const distance = spot.radius * (0.3 + r2 * 0.7);
  const fromX = spot.point.x + Math.cos(angle) * distance;
  const fromZ = spot.point.z + Math.sin(angle) * distance;

  const jumpAngle = angle + (r3 - 0.5) * 2.4;
  const jumpLength = 0.8 + r4 * 1.4;

  return {
    // Nepravidelný posun uvnitř přihrádky — bez něj by ryby vyskakovaly
    // na takt jako metronom.
    start: slot * SLOT_SECONDS + r5 * SLOT_SECONDS,
    duration: 0.7 + r6 * 0.55,
    fromX,
    fromZ,
    toX: fromX + Math.cos(jumpAngle) * jumpLength,
    toZ: fromZ + Math.sin(jumpAngle) * jumpLength,
    height: 0.5 + r2 * 0.9,
    spin: (r3 - 0.5) * 1.2,
  };
}

function applyJump(holder: Object3D, jump: Jump, t: number): void {
  // Parabola: v krajích na hladině, uprostřed nejvýš.
  const arc = 4 * t * (1 - t);
  holder.position.set(
    jump.fromX + (jump.toX - jump.fromX) * t,
    WORLD.seaLevel - 0.15 + arc * jump.height,
    jump.fromZ + (jump.toZ - jump.fromZ) * t,
  );

  // Sklon podle tečny paraboly — ryba míří tam, kam letí, a při dopadu
  // má hlavu dolů.
  const slope = jump.height * 4 * (1 - 2 * t);
  const horizontal = Math.hypot(jump.toX - jump.fromX, jump.toZ - jump.fromZ) || 1;
  holder.rotation.set(
    -Math.atan2(slope, horizontal),
    Math.atan2(jump.toX - jump.fromX, jump.toZ - jump.fromZ),
    jump.spin * t,
  );
}

function mod(value: number, length: number): number {
  return ((value % length) + length) % length;
}

/** Celočíselný hash dvou souřadnic. Musí dát stejný výsledek všude. */
function hash2(x: number, y: number): number {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

/** Deterministické „náhodné" číslo 0..1 ze seedu, přihrádky a pořadí. */
function random01(seed: number, slot: number, index: number): number {
  return hash2(Math.imul(seed, 31) + slot, Math.imul(index, 2654435761)) / 4294967296;
}
